import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useLicense } from "../context/LicenseContext";

/**
 * Full-screen lock when the appliance license is hard-locked (expired past
 * grace, revoked, or trial ended). Replaces the app so a locked appliance
 * shows a clear recovery screen instead of pages full of 402 errors / infinite
 * spinners.
 *
 * Exempt paths still render normally so recovery is possible — most importantly
 * /license (activate a new token; an always-open API prefix) plus the auth
 * routes and /security (MFA enrollment).
 */

const EXEMPT_PREFIXES = [
  "/license",
  "/login",
  "/security",
  "/forgot-password",
  "/reset-password",
  "/sso/return",
];

const LOCK_COPY: Record<string, { title: string; body: string }> = {
  expired: {
    title: "License expired",
    body: "This appliance's license has expired and is past its grace period. Activate a current license token to restore access.",
  },
  revoked: {
    title: "License revoked",
    body: "This appliance's license has been revoked. Contact your provider, then activate a new license token.",
  },
  trial_expired: {
    title: "Free trial ended",
    body: "Your 30-day free trial has ended. Activate a license token to continue using the appliance.",
  },
};

export default function LicenseGate({ children }: { children: React.ReactNode }) {
  const { locked, lockStatus } = useLicense();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const exempt = EXEMPT_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
  );
  if (!locked || exempt) return <>{children}</>;

  const copy = LOCK_COPY[lockStatus || "expired"] || LOCK_COPY.expired;
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900">{copy.title}</h1>
        <p className="mt-2 text-sm text-gray-600">{copy.body}</p>

        <div className="mt-6 flex flex-col gap-2">
          {isAdmin ? (
            <button
              onClick={() => navigate("/license")}
              className="w-full px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium"
            >
              Go to License page
            </button>
          ) : (
            <p className="text-sm text-gray-500">
              Contact your administrator to renew the license for this appliance.
            </p>
          )}
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
